import { useCallback, useContext, useEffect, useRef } from 'react'
import { TryShowModalContext } from 'contexts/ModalProvider'
import usePrevious from 'hooks/usePrevious'
import noop from 'lib/function/noop'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import TripChatMobileCurrentTripActionsSheet from '../components/TripChatMobileCurrentTripActionsSheet'

export type TripChatMobileCurrentTripActionsSheetCloseSource = 'external' | 'ui'

interface UseTripChatMobileCurrentTripActionsSheetArgs {
  chatId: number | undefined
  closeSignal?: number
  persistedTitle?: string
  title: string
  onRenameSuccess?: (name: string) => void
  onSheetOpenChange?: (isOpen: boolean, closeSource?: TripChatMobileCurrentTripActionsSheetCloseSource) => void
}

export function useTripChatMobileCurrentTripActionsSheet({
  chatId,
  closeSignal,
  persistedTitle,
  title,
  onRenameSuccess = noop,
  onSheetOpenChange = noop,
}: UseTripChatMobileCurrentTripActionsSheetArgs) {
  const tryShowModal = useContext(TryShowModalContext)
  const closeSheetRef = useRef<(() => void) | undefined>(undefined)
  const closeSourceRef = useRef<TripChatMobileCurrentTripActionsSheetCloseSource>('ui')
  const isSheetOpenRef = useRef(false)
  const previousCloseSignal = usePrevious(closeSignal, closeSignal)

  useEffect(() => {
    if (closeSignal === undefined || closeSignal === previousCloseSignal) return

    closeSourceRef.current = 'external'
    closeSheetRef.current?.()
  }, [closeSignal, previousCloseSignal])

  const closeSheet = useCallback(() => {
    closeSourceRef.current = 'external'
    closeSheetRef.current?.()
  }, [])

  useEventSubscription('reset_chat_state', closeSheet)

  return useCallback(() => {
    if (isSheetOpenRef.current) return

    const closeCurrentSheetFromUi = () => {
      closeSourceRef.current = 'ui'
      closeSheetRef.current?.()
    }

    const sheetPromise = tryShowModal<void>((resolve) => {
      closeSheetRef.current = () => resolve()

      return (
        <TripChatMobileCurrentTripActionsSheet
          chatId={chatId}
          onClose={closeCurrentSheetFromUi}
          onRenameSuccess={onRenameSuccess}
          persistedTitle={persistedTitle}
          title={title}
        />
      )
    })

    if (!sheetPromise) {
      return
    }

    isSheetOpenRef.current = true
    onSheetOpenChange(true)

    const handleSheetSettled = () => {
      const closeSource = closeSourceRef.current
      closeSourceRef.current = 'ui'
      isSheetOpenRef.current = false
      closeSheetRef.current = undefined
      onSheetOpenChange(false, closeSource)
    }

    sheetPromise.then(handleSheetSettled, handleSheetSettled)
  }, [chatId, onRenameSuccess, onSheetOpenChange, persistedTitle, title, tryShowModal])
}
